import { useEffect, useState } from 'react';
import { adminApi } from '../../services/admin/admin.service';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { toast } from 'sonner';
import { Loader2, Plus, Trash2, Save, ArrowLeft, BookOpen } from 'lucide-react';
import { Link } from 'react-router-dom';

export default function CoursesEditor() {
    const [courses, setCourses] = useState<any[]>([]);
    const [categories, setCategories] = useState<any[]>([]);
    const [levels, setLevels] = useState<any[]>([]);
    const [loading, setLoading] = useState(true);
    const [editId, setEditId] = useState<number | null>(null);
    const [form, setForm] = useState<any>({});
    const [creating, setCreating] = useState(false);

    useEffect(() => {
        Promise.all([
            adminApi.getCourses().then(setCourses),
            adminApi.getCategories().then(setCategories),
            adminApi.getLevels().then(setLevels),
        ]).catch(e => toast.error('Erro: ' + e.message)).finally(() => setLoading(false));
    }, []);

    const startEdit = (course: any) => {
        setEditId(course.id);
        setForm({
            ...course,
            category_ids: (course.courses_categories || []).map((cc: any) => cc.category_id || cc.categories?.id),
            level_ids: (course.courses_levels || []).map((cl: any) => cl.level_id || cl.levels?.id),
        });
    };

    const startNew = () => {
        setCreating(true);
        setEditId(-1);
        setForm({ title: '', description: '', url: '', category_ids: [], level_ids: [] });
    };

    const cancel = () => { setEditId(null); setCreating(false); setForm({}); };

    const save = async () => {
        try {
            const result = await adminApi.upsertCourse(form);
            if (editId === -1) {
                // Reload to get full relations
                const updated = await adminApi.getCourses();
                setCourses(updated);
            } else {
                const updated = await adminApi.getCourses();
                setCourses(updated);
            }
            toast.success('Curso salvo!');
            cancel();
        } catch (e: any) { toast.error(e.message); }
    };

    const remove = async (id: number) => {
        if (!confirm('Remover este curso?')) return;
        try {
            await adminApi.deleteCourse(id);
            setCourses(courses.filter(c => c.id !== id));
            toast.success('Curso removido!');
        } catch (e: any) { toast.error(e.message); }
    };

    const toggleCatId = (catId: number) => {
        const ids = form.category_ids || [];
        setForm({ ...form, category_ids: ids.includes(catId) ? ids.filter((i: number) => i !== catId) : [...ids, catId] });
    };

    const toggleLevelId = (lvlId: number) => {
        const ids = form.level_ids || [];
        setForm({ ...form, level_ids: ids.includes(lvlId) ? ids.filter((i: number) => i !== lvlId) : [...ids, lvlId] });
    };

    if (loading) return <div className="flex items-center justify-center p-12"><Loader2 className="h-8 w-8 animate-spin text-orange-600" /></div>;

    const renderForm = () => (
        <div className="space-y-3 p-4 border-2 border-dashed border-orange-200 rounded-lg">
            <div>
                <label className="text-sm font-medium">Título</label>
                <Input value={form.title || ''} onChange={e => setForm({ ...form, title: e.target.value })} className="mt-1" />
            </div>
            <div>
                <label className="text-sm font-medium">Descrição</label>
                <Textarea value={form.description || ''} onChange={e => setForm({ ...form, description: e.target.value })} className="mt-1" />
            </div>
            <div>
                <label className="text-sm font-medium">URL</label>
                <Input value={form.url || ''} onChange={e => setForm({ ...form, url: e.target.value })} className="mt-1" />
            </div>
            <div>
                <label className="text-sm font-medium">Alt da Imagem</label>
                <Input value={form.image_alt || ''} onChange={e => setForm({ ...form, image_alt: e.target.value })} className="mt-1" />
            </div>
            <div>
                <label className="text-sm font-medium">Imagem (URL)</label>
                <Input value={form.image || ''} onChange={e => setForm({ ...form, image: e.target.value })} className="mt-1" />
            </div>
            <div>
                <label className="text-sm font-medium mb-2 block">Categorias</label>
                <div className="flex flex-wrap gap-2">
                    {categories.map(c => (
                        <button key={c.id} type="button"
                            className={`text-xs px-3 py-1 rounded-full border transition-all ${(form.category_ids || []).includes(c.id) ? 'bg-blue-500 text-white border-blue-500' : 'bg-white text-gray-600 border-gray-200 hover:border-blue-300'}`}
                            onClick={() => toggleCatId(c.id)}
                        >{c.title}</button>
                    ))}
                </div>
            </div>
            <div>
                <label className="text-sm font-medium mb-2 block">Níveis</label>
                <div className="flex flex-wrap gap-2">
                    {levels.map(l => (
                        <button key={l.id} type="button"
                            className={`text-xs px-3 py-1 rounded-full border transition-all ${(form.level_ids || []).includes(l.id) ? 'bg-purple-500 text-white border-purple-500' : 'bg-white text-gray-600 border-gray-200 hover:border-purple-300'}`}
                            onClick={() => toggleLevelId(l.id)}
                        >{l.title}</button>
                    ))}
                </div>
            </div>
            <div className="flex gap-2 pt-2">
                <Button onClick={save}><Save className="h-4 w-4 mr-1" />Salvar</Button>
                <Button variant="ghost" onClick={cancel}>Cancelar</Button>
            </div>
        </div>
    );

    return (
        <div>
            <div className="flex items-center gap-3 mb-6">
                <Link to="/admin/dashboard"><ArrowLeft className="h-5 w-5 text-gray-500 hover:text-gray-800" /></Link>
                <h1 className="text-2xl font-bold">Cursos</h1>
                <div className="flex-1" />
                <Button onClick={startNew}><Plus className="h-4 w-4 mr-1" />Novo Curso</Button>
            </div>

            {creating && editId === -1 && (
                <Card className="mb-4">
                    <CardHeader><CardTitle>Novo Curso</CardTitle></CardHeader>
                    <CardContent>{renderForm()}</CardContent>
                </Card>
            )}

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                {courses.map(course => (
                    <Card key={course.id}>
                        <CardContent className="pt-4">
                            {editId === course.id ? renderForm() : (
                                <div>
                                    <div className="flex items-start justify-between">
                                        <div className="flex items-start gap-3">
                                            <BookOpen className="h-5 w-5 text-orange-500 mt-0.5 shrink-0" />
                                            <div>
                                                <h3 className="font-semibold">{course.title}</h3>
                                                <p className="text-sm text-gray-500 mt-1 line-clamp-2">{course.description}</p>
                                                {course.url && <a href={course.url} target="_blank" className="text-xs text-blue-500 hover:underline">{course.url}</a>}
                                            </div>
                                        </div>
                                        <div className="flex gap-1 shrink-0">
                                            <Button size="sm" variant="ghost" onClick={() => startEdit(course)}>Editar</Button>
                                            <Button size="sm" variant="ghost" className="text-red-500" onClick={() => remove(course.id)}><Trash2 className="h-4 w-4" /></Button>
                                        </div>
                                    </div>
                                    <div className="flex flex-wrap gap-1 mt-3">
                                        {(course.courses_categories || []).map((cc: any) => (
                                            <span key={cc.id} className="text-xs bg-blue-50 text-blue-600 px-2 py-0.5 rounded">{cc.categories?.title || `Cat ${cc.category_id}`}</span>
                                        ))}
                                        {(course.courses_levels || []).map((cl: any) => (
                                            <span key={cl.id} className="text-xs bg-purple-50 text-purple-600 px-2 py-0.5 rounded">{cl.levels?.title || `Nível ${cl.level_id}`}</span>
                                        ))}
                                    </div>
                                </div>
                            )}
                        </CardContent>
                    </Card>
                ))}
            </div>

            {courses.length === 0 && !creating && (
                <div className="text-center py-12 text-gray-400">
                    <BookOpen className="h-12 w-12 mx-auto mb-3 opacity-50" />
                    <p>Nenhum curso cadastrado</p>
                </div>
            )}
        </div>
    );
}
